import { useRef, useCallback, useState, useEffect, createElement } from 'react';
import { useToast, Alert, AlertIcon, AlertTitle, CloseButton } from '@chakra-ui/react';
import imageCompression from 'browser-image-compression';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import type {
  CloudinaryUploadWidget,
  CloudinaryUploadWidgetResult,
  CloudinaryUploadWidgetError,
  CloudinaryUploadWidgetCoordinates
} from '@/types/cloudinary';

const CLOUDINARY_CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET;

// Upload result returned to consumers
export interface CloudinaryUploadResult {
  url: string;
  publicId: string;
  thumbnailUrl?: string;
  mediaType: 'image' | 'video';
  duration?: number;
}

export interface UseCloudinaryUploadOptions {
  // Upload method
  mode?: 'widget' | 'direct';

  // File settings
  maxFileSize?: number;
  allowedFormats?: string[];
  resourceType?: 'image' | 'video' | 'auto';

  // Cropping (widget mode only)
  cropping?: boolean;
  aspectRatio?: number;

  // Video validation (direct mode only)
  minVideoDuration?: number;
  maxVideoDuration?: number;

  /**
   * Show a low-key, dismissible toast ("No photo selected") when a photo
   * pick is abandoned without a successful upload. Opt-in only — defaults
   * to `false`/undefined so existing callers (e.g. `CreatePostForm.tsx`)
   * see zero behavior change. Meaningful in BOTH modes:
   *  - `mode: 'widget'` — fires when a widget session reaches `close` after
   *    `opened` with no intervening `success`/`error`.
   *  - `mode: 'direct'` — the native file input has no equivalent `close`
   *    event, so this is detected heuristically: if a file selection is
   *    still pending (no `onchange`/`oncancel` resolution) when the app
   *    regains foreground, and stays pending past a short grace period, the
   *    result is assumed to have been silently dropped (e.g. a Capacitor
   *    WebView losing the native picker's result during backgrounding).
   *    Foreground-regain is detected via TWO independent signals — the
   *    page-visibility `visibilitychange` event AND Capacitor's
   *    `appStateChange` event — since it was never verified whether
   *    presenting the native photo picker over a WKWebView reliably fires a
   *    page-visibility transition (the picker sheet is often a modal within
   *    the still-foregrounded app, not a true visibility change). Both
   *    signals feed the same check, deduped so a near-simultaneous pair
   *    cannot double-fire the toast.
   */
  notifyOnEmptyClose?: boolean;

  /**
   * Stamps the direct-mode upload with an owner id (Cloudinary `context`
   * metadata: `uploader=<id>`) so a later discard call can be checked for
   * ownership server-side. Opt-in, off by default — omitting it preserves
   * exact existing behavior for every other caller of this hook.
   */
  ownerId?: number;

  // Callbacks
  onSuccess: (result: CloudinaryUploadResult) => void;
  onError?: (error: Error) => void;
}

/**
 * Extracts the first custom crop box from a widget upload result's
 * `coordinates` field, validating its shape defensively since it comes from
 * an external SDK response.
 */
function extractFirstCropBox(coordinates: unknown): [number, number, number, number] | null {
  if (typeof coordinates !== 'object' || coordinates === null) {
    return null;
  }

  const custom = (coordinates as CloudinaryUploadWidgetCoordinates).custom;
  if (!Array.isArray(custom) || custom.length === 0) {
    return null;
  }

  const rawBox = custom[0];
  if (
    !Array.isArray(rawBox) ||
    rawBox.length !== 4 ||
    !rawBox.every((n) => typeof n === 'number' && Number.isFinite(n))
  ) {
    return null;
  }

  // Cloudinary's crop transformation rejects fractional coordinates (400:
  // "Invalid crop coordinates") — round to the nearest integer pixel.
  // Empirically verified against live Cloudinary in this round's review.
  const box = rawBox.map(Math.round) as [number, number, number, number];

  // A zero (or negative, defensively) width/height box is degenerate —
  // Cloudinary rejects it (400: "Invalid width in transformation: 0") and
  // there is no sane crop to apply, so fall back to the uncropped original
  // rather than persisting a permanently broken delivery URL.
  const [, , width, height] = box;
  if (width <= 0 || height <= 0) {
    return null;
  }

  return box;
}

/**
 * Builds a Cloudinary delivery URL that reflects the member's actual crop
 * selection, instead of the full uncropped original the widget's `success`
 * callback returns by default.
 *
 * Background: with `cropping: true`, the Upload Widget's `secure_url` is
 * ALWAYS the full original image — the crop the member drew is only
 * available as metadata on `result.info.coordinates.custom` (verified
 * against Cloudinary docs, Context7 `/websites/cloudinary`: an array of
 * `[x, y, width, height]` boxes in ORIGINAL-IMAGE pixel space, with `x, y`
 * as the TOP-LEFT corner of the region — not center-origin. This was not
 * additionally cross-checked against a live widget response in this
 * session; if a future bug report suggests coordinates are off, verify the
 * live shape before assuming this function is at fault).
 *
 * Inserts a `c_crop,x_<x>,y_<y>,w_<w>,h_<h>` transformation segment
 * immediately after `/upload/` in the delivery URL.
 *
 * IMPORTANT: no gravity token (e.g. `g_custom`) is included here. Verified
 * against Cloudinary's transformation docs (Context7 `/websites/cloudinary`,
 * "How to offset face-detection gravity when cropping"): when a gravity
 * mode is combined with `c_crop`, `x`/`y` do NOT act as fixed/absolute
 * top-left coordinates — they become an offset relative to the
 * gravity-derived anchor (and are frequently no-ops, as empirically
 * verified against a live Cloudinary demo cloud: `c_crop,g_custom,x_0,y_0,
 * w_200,h_200` produced an identical result to `c_crop,g_custom,w_200,
 * h_200`). Since `coordinates.custom` boxes are already absolute
 * original-image pixel coordinates (top-left origin — see above), the
 * correct delivery transformation is a direct rectangle crop with NO
 * gravity: `c_crop` + explicit `x`/`y`/`w`/`h` crops exactly that pixel
 * region from the top-left origin, per the "Cropping with Coordinates"
 * doc section.
 *
 * Returns `secureUrl` UNCHANGED (never `null`) when no valid crop box is
 * present — covers the "member didn't crop" case, malformed/missing
 * `coordinates`, and delivery URLs that don't contain an `/upload/` segment
 * to anchor the transformation on. Callers can therefore always use the
 * return value directly with no extra fallback branch.
 */
export function buildCroppedDeliveryUrl(secureUrl: string, coordinates: unknown): string {
  const box = extractFirstCropBox(coordinates);
  if (!box) {
    return secureUrl;
  }

  const uploadMarker = '/upload/';
  const uploadIndex = secureUrl.indexOf(uploadMarker);
  if (uploadIndex === -1) {
    return secureUrl;
  }

  const [x, y, width, height] = box;
  const transformation = `c_crop,x_${x},y_${y},w_${width},h_${height}`;
  const insertAt = uploadIndex + uploadMarker.length;

  return `${secureUrl.slice(0, insertAt)}${transformation}/${secureUrl.slice(insertAt)}`;
}

/** Stable id for the "No photo selected" toast — dedupes repeated cancels instead of stacking. */
const EMPTY_CLOSE_TOAST_ID = 'cloudinary-empty-close';

/**
 * Grace period between the app regaining focus/visibility and concluding a
 * still-pending direct-mode file selection was silently dropped. `onchange`
 * can fire asynchronously right around the same time as focus regain on a
 * normal successful pick, so an immediate check would false-positive on
 * every ordinary selection — this window lets that resolution land first.
 */
const DIRECT_MODE_FOCUS_GRACE_PERIOD_MS = 500;

/**
 * Dedupe window for the two foreground-regain signals (`visibilitychange`
 * and Capacitor's `appStateChange`) — when both fire for the same real
 * transition in quick succession, only the first is allowed to (re)schedule
 * the grace-period timer.
 */
const FOREGROUND_SIGNAL_DEDUPE_MS = 100;

/**
 * Shows the shared "No photo selected" toast used by both the widget-mode
 * `close`-without-`success` path and the direct-mode focus-regain heuristic.
 * Guarded by a stable `id` + `isActive` check so repeated triggers (e.g. a
 * member cancelling twice in quick succession) update/dedupe rather than
 * stacking multiple toasts.
 */
function showNoPhotoSelectedToast(toast: ReturnType<typeof useToast>) {
  if (toast.isActive(EMPTY_CLOSE_TOAST_ID)) {
    return;
  }
  toast({
    id: EMPTY_CLOSE_TOAST_ID,
    render: ({ onClose }: { onClose: () => void }) =>
      createElement(
        Alert,
        { status: 'info', borderRadius: 'md', alignItems: 'center', boxShadow: 'lg' },
        createElement(AlertIcon),
        createElement(AlertTitle, { flex: '1', mr: 2, fontSize: 'sm' }, 'No photo selected'),
        createElement(CloseButton, {
          onClick: onClose,
          minW: '44px',
          minH: '44px',
          'aria-label': 'Dismiss notification'
        })
      )
  });
}

const DEFAULT_IMAGE_FORMATS = ['jpg', 'jpeg', 'png', 'webp'];
const DEFAULT_MAX_FILE_SIZE = 5_000_000; // 5MB — controls widget mode and callers that have not opted into the 30MB window

/** Hard ceiling for accepted files — above this, reject immediately */
const MAX_ACCEPTED_FILE_SIZE = 30_000_000; // 30MB

/** Cloudinary's upload size limit — compression target for all large files */
const CLOUDINARY_UPLOAD_LIMIT = 5_000_000; // 5MB

/**
 * Scale compression dimensions based on source file size.
 * Assumes a 5MB compression target — larger files require more aggressive downscaling.
 *
 * Tiers extended for the 30MB MAX_ACCEPTED_FILE_SIZE ceiling (was 12MB) — the
 * original top tier (`> 10_000_000 return 900`) flatlined at 900px for
 * anything above 10MB, which under-scaled modern 15-30MB phone camera
 * output relative to the existing step pattern. Added two more steps
 * following the same ~30-40% reduction cadence as the tiers below them.
 */
function compressionDimensions(fileSizeBytes: number): number {
  if (fileSizeBytes > 20_000_000) return 700;
  if (fileSizeBytes > 15_000_000) return 800;
  if (fileSizeBytes > 10_000_000) return 900;
  if (fileSizeBytes > 8_000_000) return 1200;
  if (fileSizeBytes > 5_000_000) return 1600;
  return 2048;
}

export function useCloudinaryUpload({
  mode = 'widget',
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  allowedFormats = DEFAULT_IMAGE_FORMATS,
  resourceType = 'image',
  cropping = false,
  aspectRatio = 1,
  minVideoDuration,
  maxVideoDuration,
  notifyOnEmptyClose = false,
  ownerId,
  onSuccess,
  onError
}: UseCloudinaryUploadOptions) {
  const toast = useToast();
  const widgetRef = useRef<CloudinaryUploadWidget | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  /** File selected in direct mode, waiting for member confirmation before upload begins */
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  /**
   * Tracks whether the current widget session has reached `success` or
   * `error`. Reset on every `opened` event (start of a fresh session) so a
   * stale `close` emitted by `widgetRef.current.destroy()` tearing down a
   * PREVIOUS session cannot false-positive the empty-close toast for a new
   * one — see `openUploadWidget`'s `destroy()` call below.
   */
  const widgetSessionResolvedRef = useRef(false);
  /**
   * Direct mode's analogue to `widgetSessionResolvedRef` — `true` while a
   * native file-picker selection is outstanding (picker opened, no
   * `onchange`/`oncancel` resolution yet). Used by the focus/visibility
   * heuristic below to detect a silently-dropped picker result.
   */
  const pendingFileSelectionRef = useRef(false);
  /** Handle for the in-flight grace-period timer, so a rapid re-trigger cancels the previous one instead of stacking. */
  const emptyCloseGraceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Timestamp of the last processed foreground-regain signal — see `FOREGROUND_SIGNAL_DEDUPE_MS`. */
  const lastForegroundSignalAtRef = useRef(0);

  // ============================================
  // WIDGET MODE - Uses Cloudinary Upload Widget
  // ============================================
  const openUploadWidget = useCallback(() => {
    if (mode !== 'widget') {
      console.warn('openUploadWidget called but mode is not "widget"');
      return;
    }

    if (!window.cloudinary) {
      toast({
        title: 'Upload service unavailable',
        description: 'Please refresh the page and try again.',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    if (widgetRef.current) {
      widgetRef.current.destroy();
    }

    widgetRef.current = window.cloudinary.createUploadWidget(
      {
        cloudName: CLOUDINARY_CLOUD_NAME,
        uploadPreset: CLOUDINARY_UPLOAD_PRESET,
        sources: ['local'],
        multiple: false,
        cropping,
        croppingAspectRatio: aspectRatio,
        croppingShowDimensions: true,
        maxFileSize,
        clientAllowedFormats: allowedFormats,
        resourceType: resourceType === 'auto' ? undefined : resourceType,
        styles: {
          palette: {
            window: '#1A202C',
            windowBorder: '#2D3748',
            tabIcon: '#E2E8F0',
            menuIcons: '#A0AEC0',
            textDark: '#1A202C',
            textLight: '#E2E8F0',
            link: '#805AD5',
            action: '#805AD5',
            inactiveTabIcon: '#4A5568',
            error: '#E53E3E',
            inProgress: '#805AD5',
            complete: '#38A169',
            sourceBg: '#2D3748'
          }
        }
      },
      (error: CloudinaryUploadWidgetError | null, result: CloudinaryUploadWidgetResult) => {
        if (error) {
          console.error('Cloudinary upload error:', error);
          widgetSessionResolvedRef.current = true;
          toast({
            title: 'Upload failed',
            description: error.message || 'Failed to upload',
            status: 'error',
            duration: 3000,
            isClosable: true
          });
          onError?.(new Error(error.message || 'Upload failed'));
          setIsUploading(false);
          return;
        }

        if (result.event === 'success' && result.info) {
          widgetSessionResolvedRef.current = true;
          // Only widget-mode + cropping calls can have a crop selection to recover — leave
          // non-cropping widget uploads untouched (buildCroppedDeliveryUrl is a no-op for them
          // anyway since coordinates would be absent, but skip the call entirely for clarity).
          const deliveryUrl = cropping
            ? buildCroppedDeliveryUrl(result.info.secure_url, result.info.coordinates)
            : result.info.secure_url;
          const uploadResult: CloudinaryUploadResult = {
            url: deliveryUrl,
            publicId: result.info.public_id,
            mediaType: result.info.resource_type === 'video' ? 'video' : 'image'
          };
          onSuccess(uploadResult);
          toast({
            title: 'Upload successful',
            status: 'success',
            duration: 2000,
            isClosable: true
          });
          setIsUploading(false);
        }

        if (result.event === 'opened') {
          widgetSessionResolvedRef.current = false;
          setIsUploading(true);
        }

        if (result.event === 'close') {
          setIsUploading(false);
          if (notifyOnEmptyClose && !widgetSessionResolvedRef.current) {
            showNoPhotoSelectedToast(toast);
          }
          // Mark this session as handled once its close has been processed —
          // in addition to the existing success/error sets above — so a
          // subsequent STALE close (emitted by widgetRef.current.destroy()
          // tearing down this now-finished session when a new one opens)
          // cannot false-positive a second empty-close toast.
          widgetSessionResolvedRef.current = true;
        }
      }
    );

    widgetRef.current.open();
  }, [mode, toast, onSuccess, onError, cropping, aspectRatio, maxFileSize, allowedFormats, resourceType, notifyOnEmptyClose]);

  // ============================================
  // DIRECT MODE - Direct API upload
  // ============================================
  const validateVideoDuration = useCallback(async (file: File): Promise<number | null> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.src = URL.createObjectURL(file);

      video.onloadedmetadata = () => {
        const duration = video.duration;
        URL.revokeObjectURL(video.src);

        if (minVideoDuration !== undefined && duration < minVideoDuration) {
          toast({
            title: 'Video too short',
            description: `Videos must be at least ${minVideoDuration} seconds.`,
            status: 'error',
            duration: 3000,
            isClosable: true
          });
          resolve(null);
          return;
        }

        if (maxVideoDuration !== undefined && duration > maxVideoDuration) {
          toast({
            title: 'Video too long',
            description: `Videos must be at most ${maxVideoDuration} seconds.`,
            status: 'error',
            duration: 3000,
            isClosable: true
          });
          resolve(null);
          return;
        }

        resolve(duration);
      };

      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        resolve(null);
      };
    });
  }, [minVideoDuration, maxVideoDuration, toast]);

  const uploadFile = useCallback(async (file: File) => {
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');

    // Validate resource type
    if (resourceType === 'image' && !isImage) {
      toast({
        title: 'Invalid file type',
        description: 'Please select an image file.',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    if (resourceType === 'video' && !isVideo) {
      toast({
        title: 'Invalid file type',
        description: 'Please select a video file.',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    // Hard ceiling — reject files above 30MB immediately, before any compression attempt
    if (file.size > MAX_ACCEPTED_FILE_SIZE) {
      toast({
        title: 'File too large',
        description: 'Maximum file size is 30MB.',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    // For non-images and GIFs, compression cannot help — enforce the caller's maxFileSize directly.
    // For other images, the compression block below targets CLOUDINARY_UPLOAD_LIMIT, so we only
    // reject here when the caller wants a tighter limit than compression would produce.
    if (!isImage || file.type === 'image/gif') {
      if (maxFileSize && file.size > maxFileSize) {
        toast({
          title: 'File too large',
          description: `Maximum file size is ${(maxFileSize / 1_000_000).toFixed(0)}MB.`,
          status: 'error',
          duration: 3000,
          isClosable: true
        });
        return;
      }
    }

    // Validate file format for images before compression — no point compressing a rejected format
    if (isImage && allowedFormats.length > 0) {
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (ext && !allowedFormats.includes(ext)) {
        toast({
          title: 'Invalid file format',
          description: `Allowed formats: ${allowedFormats.join(', ')}`,
          status: 'error',
          duration: 3000,
          isClosable: true
        });
        return;
      }
    }

    // Compress images that exceed Cloudinary's 5MB upload limit (except GIFs, which cannot be recompressed)
    let fileToUpload: File = file;
    if (isImage && file.type !== 'image/gif' && file.size > CLOUDINARY_UPLOAD_LIMIT) {
      try {
        const targetSizeMB = CLOUDINARY_UPLOAD_LIMIT / 1_000_000; // always 5MB
        fileToUpload = await imageCompression(file, {
          maxSizeMB: targetSizeMB,
          maxWidthOrHeight: compressionDimensions(file.size),
          useWebWorker: true,
        });
      } catch (compressionError) {
        console.error('Image compression failed:', compressionError);
        toast({
          title: 'File too large',
          description: 'Maximum file size is 30MB and this image could not be compressed enough.',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
        return;
      }

      // imageCompression targets maxSizeMB but does not guarantee it — reject if still too large
      if (fileToUpload.size > CLOUDINARY_UPLOAD_LIMIT) {
        toast({
          title: 'File too large',
          description: 'This image could not be compressed to a small enough size. Try a lower resolution photo.',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
        return;
      }
    }

    // Validate video duration if applicable
    let duration: number | undefined;
    if (isVideo && (minVideoDuration !== undefined || maxVideoDuration !== undefined)) {
      const validatedDuration = await validateVideoDuration(file);
      if (validatedDuration === null) {
        return; // Validation failed, error already shown
      }
      duration = validatedDuration;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', fileToUpload);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      if (ownerId !== undefined) {
        formData.append('context', `uploader=${ownerId}`);
      }

      const uploadType = isVideo ? 'video' : 'image';
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/${uploadType}/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Upload failed');
      }

      if (data.secure_url) {
        const uploadResult: CloudinaryUploadResult = {
          url: data.secure_url,
          publicId: data.public_id,
          mediaType: isVideo ? 'video' : 'image',
          duration
        };

        // Generate thumbnail URL for videos
        if (isVideo) {
          uploadResult.thumbnailUrl = data.secure_url.replace(
            /\.(mp4|mov|webm|avi)$/i,
            '.jpg'
          );
        }

        onSuccess(uploadResult);
        toast({
          title: 'Upload successful',
          status: 'success',
          duration: 2000,
          isClosable: true
        });
      }
    } catch (error) {
      console.error('Cloudinary upload error:', error);
      const err = error instanceof Error ? error : new Error('Upload failed');
      toast({
        title: 'Upload failed',
        description: err.message,
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      onError?.(err);
    } finally {
      setIsUploading(false);
    }
  }, [resourceType, maxFileSize, allowedFormats, minVideoDuration, maxVideoDuration, validateVideoDuration, ownerId, onSuccess, onError, toast]);

  const openFilePicker = useCallback(() => {
    // A fast double-pick (e.g. re-tapping before the previous selection
    // resolved) would otherwise leave the previous grace-period timer
    // in-flight, which can fire a spurious "No photo selected" toast for
    // THIS session even though this pick later resolves fine.
    if (emptyCloseGraceTimerRef.current) {
      clearTimeout(emptyCloseGraceTimerRef.current);
      emptyCloseGraceTimerRef.current = null;
    }

    const input = document.createElement('input');
    input.type = 'file';

    // Build accept string based on resourceType
    if (resourceType === 'image') {
      input.accept = allowedFormats.map(fmt => `.${fmt}`).join(',');
    } else if (resourceType === 'video') {
      input.accept = 'video/*';
    } else {
      input.accept = 'image/*,video/*';
    }

    input.style.display = 'none';

    input.onchange = (e) => {
      // `onchange` firing at all is a resolved outcome (picker closed with a
      // definite state) — clears the pending-selection flag whether or not
      // a file actually came back, so the focus-regain heuristic below never
      // false-positives on a normal pick.
      pendingFileSelectionRef.current = false;
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        // A real file arrived — if the grace-period timer already fired the
        // "No photo selected" toast (e.g. iOS downloading an iCloud-optimized
        // asset before handing off the file, taking longer than the grace
        // window), retract it so the member isn't left with a stale
        // "failed" message immediately followed by "Upload successful".
        if (toast.isActive(EMPTY_CLOSE_TOAST_ID)) {
          toast.close(EMPTY_CLOSE_TOAST_ID);
        }
        // Two-step flow: store for member confirmation — upload does not begin until confirmUpload() is called
        setPendingFile(file);
      }
      document.body.removeChild(input);
    };

    input.oncancel = () => {
      // Explicit cancel — also a resolved outcome, not a silent drop.
      pendingFileSelectionRef.current = false;
      document.body.removeChild(input);
    };

    // Start tracking "a selection is pending" — cleared by onchange/oncancel
    // above. If neither fires (the Capacitor WebView drops the native
    // picker's result during backgrounding), this stays true until the
    // focus/visibility heuristic below concludes the result was dropped.
    pendingFileSelectionRef.current = true;

    document.body.appendChild(input);
    input.click();
  }, [resourceType, allowedFormats, toast]);

  // ============================================
  // DIRECT MODE — silent-drop detection (Fix 3b, hardened in D40 round 3)
  // ============================================
  // The native file input has no `close` event equivalent to the widget's.
  // If the Capacitor WebView drops the picker's result during backgrounding,
  // neither onchange nor oncancel fires, so there is otherwise zero signal.
  // Heuristic: when the app regains foreground with a selection still
  // pending, wait a short grace period (onchange can land asynchronously
  // right around foreground regain on a normal pick) and then, if still
  // pending, treat it as a silent drop.
  //
  // Foreground regain is watched via TWO independent signals:
  //  - `visibilitychange` — reliable in a plain web/PWA context.
  //  - Capacitor's `appStateChange` — added because it was never verified
  //    that presenting Capacitor's native photo-picker sheet over a
  //    WKWebView reliably fires a page-visibility transition (the picker is
  //    often a modal within the still-foregrounded app, not a true
  //    visibility change). Only registered on native platforms, mirroring
  //    the existing `appStateChange` usage in `App.tsx`.
  // Both signals funnel into the same handler, which self-dedupes via
  // `FOREGROUND_SIGNAL_DEDUPE_MS` so a near-simultaneous pair for the same
  // real transition schedules only one grace-period timer.
  useEffect(() => {
    if (mode !== 'direct' || !notifyOnEmptyClose) {
      return;
    }

    const handleForegroundRegain = () => {
      if (!pendingFileSelectionRef.current) {
        return;
      }

      const now = Date.now();
      if (now - lastForegroundSignalAtRef.current < FOREGROUND_SIGNAL_DEDUPE_MS) {
        return;
      }
      lastForegroundSignalAtRef.current = now;

      if (emptyCloseGraceTimerRef.current) {
        clearTimeout(emptyCloseGraceTimerRef.current);
      }

      emptyCloseGraceTimerRef.current = setTimeout(() => {
        emptyCloseGraceTimerRef.current = null;
        if (pendingFileSelectionRef.current) {
          pendingFileSelectionRef.current = false;
          showNoPhotoSelectedToast(toast);
        }
      }, DIRECT_MODE_FOCUS_GRACE_PERIOD_MS);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        return;
      }
      handleForegroundRegain();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    let appStateListenerHandle: ReturnType<typeof CapacitorApp.addListener> | null = null;
    if (Capacitor.isNativePlatform()) {
      appStateListenerHandle = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          handleForegroundRegain();
        }
      });
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      appStateListenerHandle?.then((listener) => listener.remove());
      if (emptyCloseGraceTimerRef.current) {
        clearTimeout(emptyCloseGraceTimerRef.current);
        emptyCloseGraceTimerRef.current = null;
      }
    };
  }, [mode, notifyOnEmptyClose, toast]);

  /**
   * Confirms the pending file selection and begins the upload.
   * Only relevant in direct mode — call this when the member taps "Use This Photo".
   */
  const confirmUpload = useCallback(async () => {
    if (!pendingFile) return;
    const fileToUpload = pendingFile;
    setPendingFile(null); // clear before uploading so UI reflects upload-in-progress state
    await uploadFile(fileToUpload);
  }, [pendingFile, uploadFile]);

  /** Discards the pending file selection without uploading. */
  const clearPendingFile = useCallback(() => {
    setPendingFile(null);
  }, []);

  return {
    openUploadWidget,
    uploadFile,
    openFilePicker,
    isUploading,
    pendingFile,
    confirmUpload,
    clearPendingFile,
  };
}
